import { useState, useEffect, useRef } from "react";
import { Video, Loader2, Check, Camera, User, FileText, Upload, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TutorSettings = () => {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", bio: "", photo_url: "", zoom_link: "", max_students: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileRef = useRef(null);
  const token = localStorage.getItem("access_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/auth/tutor/me`, { headers });
        const t = res.data;
        setForm({
          name: t.name || "",
          bio: t.bio || "",
          photo_url: t.photo_url || "",
          zoom_link: t.zoom_link || "",
          max_students: t.max_students != null ? String(t.max_students) : "",
        });
      } catch {
        if (user) {
          setForm({
            name: user.name || "",
            bio: user.bio || "",
            photo_url: user.photo_url || "",
            zoom_link: user.zoom_link || "",
            max_students: user.max_students != null ? String(user.max_students) : "",
          });
        }
      }
    };
    fetchProfile();
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload/photo`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, photo_url: res.data.url }));
      toast.success("Photo uploaded!");
    } catch {
      toast.error("Failed to upload photo");
      setPreviewUrl("");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        max_students: form.max_students !== "" ? parseInt(form.max_students, 10) : null,
      };
      const res = await axios.put(`${API}/auth/tutor/profile`, payload, { headers });
      setUser(res.data);
      toast.success("Profile updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const displayPhoto = previewUrl || form.photo_url;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Settings</h2>
      <div className="brutalist-card p-6 max-w-lg space-y-6">

        {/* Photo */}
        <div className="flex items-center gap-5">
          <div
            className="w-20 h-20 rounded-full border-2 border-[#0A0A0A] overflow-hidden bg-[#F0EFEB] flex items-center justify-center cursor-pointer relative group"
            onClick={() => fileRef.current?.click()}
            data-testid="settings-photo-container"
          >
            {displayPhoto ? (
              <img src={displayPhoto} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <User className="w-8 h-8 text-[#999]" />
            )}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full">
              {uploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
            </div>
          </div>
          <div>
            <p className="font-bold text-sm">Profile Photo</p>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="text-xs text-[#3C8DE4] hover:underline flex items-center gap-1 mt-1"
              data-testid="settings-upload-photo-btn"
            >
              <Upload className="w-3 h-3" /> {uploading ? "Uploading..." : "Change photo"}
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
        </div>

        {/* Name */}
        <div>
          <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-1">
            <User className="w-4 h-4" /> Name
          </Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="Your full name"
            className="brutalist-input mt-1"
            data-testid="settings-name-input"
          />
        </div>

        {/* Bio */}
        <div>
          <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-1">
            <FileText className="w-4 h-4" /> Bio
          </Label>
          <textarea
            value={form.bio}
            onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
            placeholder="Tell students about yourself..."
            rows={3}
            className="brutalist-input mt-1 w-full resize-none"
            data-testid="settings-bio-input"
          />
        </div>

        {/* Zoom Link */}
        <div>
          <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-1">
            <Video className="w-4 h-4" /> Zoom Meeting Link
          </Label>
          <p className="text-xs text-[#4A4A4A] mb-1">This link is sent to students when they book a class with you.</p>
          <Input
            value={form.zoom_link}
            onChange={(e) => setForm((p) => ({ ...p, zoom_link: e.target.value }))}
            placeholder="https://zoom.us/j/your-meeting-id"
            className="brutalist-input mt-1"
            data-testid="settings-zoom-input"
          />
        </div>

        {/* Student Capacity */}
        <div>
          <Label className="text-sm font-bold uppercase tracking-wider flex items-center gap-1">
            <Users className="w-4 h-4" /> Student Capacity
          </Label>
          <p className="text-xs text-[#4A4A4A] mb-1">Maximum number of students you can take. Leave blank for unlimited.</p>
          <Input
            type="number"
            min="1"
            max="50"
            value={form.max_students}
            onChange={(e) => setForm((p) => ({ ...p, max_students: e.target.value }))}
            placeholder="e.g. 5 (leave blank for no limit)"
            className="brutalist-input mt-1"
            data-testid="settings-max-students-input"
          />
        </div>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saving || uploading}
          className="brutalist-btn-primary flex items-center gap-2"
          data-testid="save-settings-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Check className="w-4 h-4" /> Save Changes</>}
        </button>
      </div>
    </div>
  );
};

export default TutorSettings;
